import React, { useEffect, useMemo, useRef, useState } from 'react';
import ChartRadial from '@/components/charts/RadialNew';
import Header2 from '@/imgs/AssetAllocation/header2.png';
import Summary from '@/components/Summary';
import NoData from '@/components/NoData';
import CustomTooltip from '@/components/CustomTooltip';
import HeaderSection from './HeaderSection';
import PerformanceTable from './PerformanceTable';
import ProductCard from './ProductCard';
import { useAssetAllocationCharts } from '../../hooks/useAssetAllocationCharts';
import useCopilotValueStore from '@/stores/copilotValueStore';
import { ProductItem } from '../../types/assetAllocation';

const AssetAllocation: React.FC = () => {
  const { chartData, getRiskLevelColor, mapLevelToHeader } = useAssetAllocationCharts();

  const { copilotValue4 } = useCopilotValueStore();

  // 获取产品列表
  const productList = useMemo(() => {
    const filtered = copilotValue4?.filtered || [];
    return filtered.reduce((acc, item) => {
      if (acc[item.type]) {
        acc[item.type].push(item as ProductItem);
      } else {
        acc[item.type] = [item as ProductItem];
      }
      return acc;
    }, {} as Record<string, ProductItem[]>);
    // return copilotValue4?.filtered || [];
  }, [copilotValue4]);

  // 获取评估内容
  const evaluationContent = useMemo(() => {
    return copilotValue4?.evaluation?.evaluation || '';
  }, [copilotValue4]);

  const customTooltipEle = useRef<HTMLDivElement>(null);
  const [openTooltip, setOpenTooltip] = useState(false);
  useEffect(() => {
    if (chartData.length > 0) {
      setTimeout(() => {
        const element = document.querySelector(
          '.aspect-allocation-chart-radial .recharts-area'
        ) as HTMLDivElement;

        const wrapperElement = document.querySelector(
          '.recharts-responsive-container'
        ) as HTMLDivElement;

        if (element && wrapperElement) {
          customTooltipEle.current = element;

          wrapperElement.addEventListener('mousemove', (event: MouseEvent) => {
            const rect = element.getBoundingClientRect();
            const isInElement =
              event.clientX >= rect.left &&
              event.clientX <= rect.right &&
              event.clientY >= rect.top &&
              event.clientY <= rect.bottom;
            console.log(isInElement, element.classList.contains('custome-tooltip'));
            setOpenTooltip(isInElement || element.classList.contains('custome-tooltip'));
          });
        }
      }, 1000);
    }
  }, [chartData]);

  useEffect(() => {
    if (openTooltip) {
      setTimeout(() => {
        const customContentWrapper = document.querySelector(
          '.customContentWrapper'
        ) as HTMLDivElement;
        console.log(customContentWrapper, 'customContentWrapper');

        customContentWrapper?.addEventListener('mouseleave', (event: MouseEvent) => {
          console.log(event, 'wapperTooltipRef');
          setOpenTooltip(false);
        });
      }, 100);
    }
  }, [openTooltip]);

  const customContent = () => {
    let percentage = 0;
    return (
      <div className="customContentWrapper relative">
        {chartData.map((item, index) => {
          const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);
          if (index === chartData.length - 1) {
            percentage = 100 - percentage;
          } else {
            percentage = parseFloat(((item.value / totalValue) * 100).toFixed(2));
          }
          return (
            <div key={index}>
              <div className="my-[6px] flex h-[20px] items-center bg-white px-2 text-xs leading-[20px]">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-sm`} style={{ backgroundColor: item.color }} />
                    <span className="text-[#1F1F1F]">{item.name}</span>
                  </div>
                  <span className="whitespace-nowrap text-[#1F1F1F]">
                    {item.value}万（{percentage}%）
                  </span>
                </div>
                <span className="whitespace-nowrap text-[#5959EB]">
                  预期收益: {index === 0 ? 8 - 12 : index === 1 ? 4 - 6 : 2 - 3}%
                </span>
              </div>
            </div>
          );
        })}
        <div
          className="absolute h-2 w-2 rotate-45 transform bg-white"
          style={{ bottom: '-10px', left: '50%', transform: 'translateX(-50%) rotate(45deg)' }}
        />
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <HeaderSection />

      <div className="mb-2 rounded-lg bg-white p-3">
        <div className="mb-2 flex h-[100px] w-full justify-center" data-chart="assetAllocation">
          {chartData.length > 0 ? (
            <ChartRadial
              data={chartData}
              legendPosition="right"
              innerRadius={30}
              wrapperClassName="aspect-allocation-chart-radial"
              outerRadius={50}
              cx="35%"
              cy="50%"
              legendVerticalAlign="middle"
              legendLayout="vertical"
              legendAlign="right"
              wrapperLegendStyle={{ marginRight: '30px' }}
              showTooltip={false}
              customContent={() => (
                <>
                  <text
                    x="35%"
                    y="50%"
                    dy="-10"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={12}
                    fill="#1F1F1F"
                  >
                    总结
                  </text>
                  <br />
                  <text
                    x="35%"
                    y="50%"
                    dy="10"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={12}
                    fill="#1F1F1F"
                  >
                    {copilotValue4.amount}万
                  </text>
                </>
              )}
            />
          ) : (
            <NoData className="h-[150px] w-full" />
          )}
        </div>
        {chartData.length > 0 && (
          <CustomTooltip
            element={<div ref={customTooltipEle} />}
            customContent={customContent()}
            open={openTooltip}
          />
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded">
          <img src={Header2} alt="header2" className="h-4 w-5" />
        </div>
        <span className="text-base font-semibold text-[#1F1F1F]">具体产品配置建议</span>
      </div>
      <PerformanceTable />
      <Summary isShowSuggestions={false} title="AI总结-投资策略" content={evaluationContent} />
      <div className="space-y-3">
        {Object.entries(productList).map(([type, items]) => (
          <ProductCard
            type={type}
            key={type}
            items={items}
            getRiskLevelColor={getRiskLevelColor}
            mapLevelToHeader={mapLevelToHeader}
          />
        ))}
      </div>
    </div>
  );
};

export default AssetAllocation;
